import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import type { WordCount } from "../lib/wordUtils";

type Props = {
  field?: string; // which submission field to analyze (default: answer)
  maxWords?: number;
  height?: number;
  onWordClick?: (word: string) => void;
};

const LazyReactWordcloud: any = React.lazy(() =>
  import("react-wordcloud").then((mod: any) => ({ default: mod.default ?? mod })),
);

class ErrorBoundary extends React.Component<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}, { hasError: boolean }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  override componentDidCatch(err: any, info: any) {
    console.error("WordCloud render error:", err, info);
  }
  override render() {
    if (this.state.hasError) return this.props.fallback ?? null;
    return this.props.children as any;
  }
}

function WordCloudFallback({
  words,
  onWordClick,
  minFont = 14,
  maxFont = 48,
}: {
  words: { text: string; value: number }[];
  onWordClick?: (word: string) => void;
  minFont?: number;
  maxFont?: number;
}) {
  if (!words || words.length === 0) {
    return <div className="text-sm text-muted-foreground">No data to display.</div>;
  }
  const values = words.map((w) => w.value);
  const max = Math.max(...values);
  const min = Math.min(...values);

  const scale = (v: number) => {
    if (max === min) return (minFont + maxFont) / 2;
    return (
      minFont + ((v - min) / (max - min)) * (maxFont - minFont)
    );
  };

  return (
    <div className="w-full p-4">
      <div className="flex flex-wrap gap-3 items-center justify-center">
        {words.map((w) => (
          <button
            key={w.text}
            onClick={() => onWordClick?.(w.text)}
            title={`${w.text} — ${w.value}`}
            className="inline-block leading-none text-center"
            style={{ fontSize: `${scale(w.value)}px` }}
          >
            {w.text}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function WordCloud({
  field = "answer",
  maxWords = 100,
  height = 360,
  onWordClick,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [words, setWords] = useState<WordCount[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const q = new URLSearchParams({ field, maxWords: String(maxWords) });
        const res = await fetch(`/api/submissions/wordfreq?${q.toString()}`);
        const json = await res.json();
        let freqs: WordCount[] = json?.words ?? [];

        // Validate / normalize server response to avoid runtime errors in the wordcloud library
        if (!Array.isArray(freqs)) freqs = [];
        freqs = freqs
          .map((w: any) => ({ text: String(w?.text ?? ""), value: Number(w?.value ?? 0) }))
          .filter((w) => w.text && Number.isFinite(w.value) && w.value > 0)
          .slice(0, maxWords);

        if (!cancelled) setWords(freqs);
      } catch (err) {
        console.error("WordCloud load error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [visible, field, maxWords]);

  const wcWords = useMemo(() => words.map((w) => ({ text: w.text, value: w.value })), [words]);

  const options = useMemo(
    () => ({
      rotations: 2,
      rotationAngles: [-15, 15],
      fontSizes: [14, Math.max(32, Math.min(72, Math.floor(height / 6)))],
      padding: 2,
    }),
    [height],
  );

  const callbacks = useMemo(() => ({
    getWordTooltip: (word: any) => `${word.text} (${word.value})`,
    onWordClick: (word: any) => onWordClick?.(String(word.text)),
  }), [onWordClick]);

  return (
    <Card ref={containerRef as any} className="w-full bg-white/20">
      <CardContent>
        <div role="region" aria-label="Word cloud of approved answers">
          {loading && <div className="text-sm text-muted-foreground">Loading...</div>}
          {!loading && wcWords.length === 0 && (
            <div className="text-sm text-muted-foreground">No data to display.</div>
          )}

          <div className="w-full h-full">
            {visible && wcWords.length > 0 && (
              <ErrorBoundary
                fallback={
                  <WordCloudFallback words={wcWords} onWordClick={onWordClick} />
                }
              >
                <React.Suspense fallback={<div className="text-sm text-muted-foreground">Rendering...</div>}>
                  <LazyReactWordcloud words={wcWords as any} options={options as any} callbacks={callbacks as any} />
                </React.Suspense>
              </ErrorBoundary>
            )}
          </div>

          {/* Accessible offscreen list for screen readers */}
          <ol className="sr-only" aria-hidden={false}>
            {words.map((w) => (
              <li key={w.text}>{`${w.text} — ${w.value}`}</li>
            ))}
          </ol>
        </div>
      </CardContent>
    </Card>
  );
}
